import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppErrors, NotFoundError } from '@fitness-force/errors';
import { STAFF_MODEL, STAFFINTERFACE } from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { environment } from 'apps/ff-frontend/src/environments/environment';
import { CONST_URL_SEGEMENTS } from 'libs/constants/src/lib/constants.url';
import {
  catchError,
  map,
  Observable,
  of,
  switchMap,
  take,
  throwError,
  timeout,
  finalize,
  shareReplay
} from 'rxjs';
import {
  addStaffMember,
  selectStaffById,
  selectStaffList,
  updateStaffList,
} from 'apps/ff-frontend/src/app/state/store/staff.store';

@Injectable({
  providedIn: 'root',
})
export class StaffStoreService {
  constructor(private http: HttpClient, private store: Store) {}

  private BASE_URL: string = environment.BASE_URL;
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };
  
  // Cache to store staff requests
  private staffCache: Map<number, Observable<STAFFINTERFACE>> = new Map();

  // Get all staff members from store, fetch from API if store is empty
  getAllStaff(tenantId: number): Observable<STAFFINTERFACE[]> {
    return this.store.select(selectStaffList).pipe(
      take(1),
      switchMap((staffList) => {
        if (staffList && staffList.length > 0) {
          return of(staffList);
        }
        // If store is empty, fetch from API
        return this.fetchAllStaffFromApi(tenantId);
      })
    );
  }

  private fetchAllStaffFromApi(tenantId: number): Observable<STAFFINTERFACE[]> {
    let parsedParams = new HttpParams();
    if (tenantId != 0)
      parsedParams = parsedParams.append('stf_tent_access_id', tenantId);
    parsedParams = parsedParams.append('PageNumber', 1);
    parsedParams = parsedParams.append('PageSize', 9999);
    const url = `${this.BASE_URL}${CONST_URL_SEGEMENTS.STAFF.GET_STAFF_LIST}`;
    return this.http
      .get<STAFFINTERFACE[]>(url, {
        headers: this.httpOptions.headers,
        params: parsedParams,
      })
      .pipe(
        map((staffList) => {
          this.store.dispatch(updateStaffList({ staffList }));
          return staffList;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  
  // Get staff by ID (checks store first, then API if not found)
  getStaffById(staffId: number): Observable<STAFFINTERFACE> {
    // Check if request is already in cache
    const cachedRequest = this.staffCache.get(staffId);
    if (cachedRequest) {
      return cachedRequest;
    }

    // If not in cache, create new request
    const request = this.store.select(selectStaffById(staffId)).pipe(
      take(1),
      switchMap((staffFromStore) => {
        if (staffFromStore) {
          return of(staffFromStore);
        }

        // If not in store, fetch from API
        return this.GetStaffById(staffId).pipe(
          map((staff) => {
            // Add to store
            this.store.dispatch(addStaffMember({ staff }));
            return staff;
          })
        );
      }),
      // Share the response with all subscribers and cache it
      shareReplay(1)
    );

    // Store in cache
    this.staffCache.set(staffId, request);
    return request;
  }

  private GetStaffById(STAFF_ID: number): Observable<STAFFINTERFACE> {
    const url = `${this.BASE_URL}${CONST_URL_SEGEMENTS.STAFF.GET_STAFF_LIST}/${STAFF_ID}`;
    return this.http
      .get<STAFFINTERFACE>(url, { headers: this.httpOptions.headers })
      .pipe(
        map((res) => {
          if (res) {
            return res;
          }
          throw new NotFoundError('Staff member not found');
        }),
        catchError((error: any) => {
          // Handle network errors (status 0)
          if (!error.status) {
            return throwError(() => new AppErrors('Network error or server not responding. Please check your connection.'));
          }
          // Handle 404
          if (error.status === 404) {
            return throwError(() => new NotFoundError('Staff member not found'));
          }
          // Handle other errors
          return throwError(() => new AppErrors(`Error fetching staff member: ${error.message || 'Unknown error'}`));
        })
      );
  }
}
